import { base64UrlDecode, sha256DigestOf } from "./kernel/kernel-encoding.js";
import { KERNEL_KID_PATTERN, importEs256VerifyKey, verifyEs256 } from "./kernel/kernel-internal.js";
import { SUBMITTER_KINDS } from "./federated-token.js";
import { z } from "zod";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/build-provenance.ts
/**
* The signed build provenance of one zion-build run (feature 2026-10-15-signed-builds, D1 to D6,
* D10).
*
* A zion-build run that pushes artifacts signs one in-toto Statement v1 with an SLSA provenance v1
* predicate, wrapped in a DSSE envelope, with the Cloud KMS key `build-provenance`
* (`EC_SIGN_P256_SHA256`, so ES256). The statement names every digest the run pushed (its
* subjects) and the run facts that bind it to a CP release source (its external parameters).
*
* Three readers use this module:
*
* 1. release-engine (WS-3): the publish door reads the optional `buildProvenance` member of both
*    publish bodies, calls {@link verifyBuildProvenance} against `BUILD_PROVENANCE_JWKS`, calls
*    {@link provenanceSubjectsCover} with the bundle digest and every fixture image digest (D4),
*    and compares the external parameters with the app's live release sources (D5). A refusal
*    carries one of {@link BUILD_PROVENANCE_REFUSAL_CODES}.
* 2. `bin/verify-build-provenance.mjs` (WS-6): the platform release scripts verify the envelope
*    that `build.yml` attached to each plane's build record (D10).
* 3. The zion-build signer (outside this repo) produces the exact bytes this module accepts.
*
* The wire format, fixed here (D1):
*
* - The envelope is the DSSE JSON envelope `{ payloadType, payload, signatures: [{ keyid, sig }] }`.
*   `payloadType` is {@link BUILD_PROVENANCE_PAYLOAD_TYPE}. `payload` and `sig` are standard base64
*   (RFC 4648 section 4) with canonical padding. Exactly one signature.
* - `sig` is the ASN.1 DER form of the ECDSA P-256 SHA-256 signature over the DSSE
*   pre-authentication encoding ({@link dssePreAuthEncoding}). That is the form Cloud KMS returns
*   and the form cosign, the sigstore policy-controller and Kyverno verify, so a later runtime
*   check reads the same bytes (overview "Limits" item 1).
* - `keyid` is the `kid` of one key in the key set, the `rightsCertificateJwksSchema` document
*   that `BUILD_PROVENANCE_JWKS` holds (DEF-2: the existing key-set schema and the existing key
*   import of `verifyReleaseStatementSignature`).
*
* Every schema here is strict at every level and carries no `.transform()`, so
* `ZionStrictValidationPipe` refuses an unknown key and `createOpenApiDocument()` can represent
* the publish bodies that embed {@link buildProvenanceEnvelopeSchema}.
*
* Browser-safe: no Node builtin and no Node global. The signature check uses the WebCrypto global
* `crypto.subtle`, and the UTF-8 decode uses the `TextDecoder` global; Node 24 and every browser
* define both.
*/
/** The `_type` of every in-toto Statement v1. */
const IN_TOTO_STATEMENT_TYPE = "https://in-toto.io/Statement/v1";
/** The `predicateType` of an SLSA provenance v1 predicate. */
const SLSA_PROVENANCE_PREDICATE_TYPE = "https://slsa.dev/provenance/v1";
/**
* The DSSE `payloadType` of every build provenance envelope: the in-toto media type (ruling R-002,
* DEF-1: a string literal for the payload type). The signer passes it to
* {@link dssePreAuthEncoding}, and {@link buildProvenanceEnvelopeSchema} refuses any other value.
*/
const BUILD_PROVENANCE_PAYLOAD_TYPE = "application/vnd.in-toto+json";
/**
* The SLSA `buildDefinition.buildType` of a zion-build workflow run. It names the build template:
* a `.zion/workflows/<name>.yml` workflow that zion-build ran from a `remote-ref` transfer. The URI
* is an identifier, never fetched, and it names no environment, so the internal and the production
* zion-build sign the same value.
*/
const ZION_BUILD_PROVENANCE_BUILD_TYPE = "https://zion.witness.ai/build-types/zion-build-workflow/v1";
/**
* The SLSA `runDetails.builder.id` of every statement zion-build signs (ruling R-002). It names the
* build platform, zion-build, whose `build-provenance` key is the trust root. The URI is an
* identifier, never fetched, and it names no environment, so the internal and the production
* zion-build sign the same value. {@link buildProvenanceStatementSchema} refuses any other builder
* id (DEF-1: the narrower type).
*/
const ZION_BUILD_BUILDER_ID = "https://zion.witness.ai/builders/zion-build/v1";
/**
* The largest statement an envelope carries, in bytes: 256 KiB (overview "Security", ruling
* R-002). It bounds the decoded `payload` of the envelope, the signed statement bytes. A signer
* refuses to sign a longer statement; {@link verifyBuildProvenance} refuses it as `oversize`. 600
* subjects with 512-character names take about 360 KiB, so the signer keeps names short; a run of
* 64 images and one bundle takes under 16 KiB. The compact JSON text of an envelope that
* {@link buildProvenanceEnvelopeSchema} admits is at most {@link BUILD_PROVENANCE_MAX_PAYLOAD_LENGTH}
* plus 320 characters (96 fixed, a 128-character `keyid`, a 96-character `sig`), 349,848 in all.
*/
const BUILD_PROVENANCE_MAX_ENVELOPE_BYTES = 262144;
/**
* The longest `payload` text, in characters: the canonical padded base64 length of
* {@link BUILD_PROVENANCE_MAX_ENVELOPE_BYTES} bytes, 349,528. The envelope schema refuses a longer
* text before any decode, so one anonymous request cannot make the verifier buffer more.
*/
const BUILD_PROVENANCE_MAX_PAYLOAD_LENGTH = 4 * Math.ceil(BUILD_PROVENANCE_MAX_ENVELOPE_BYTES / 3);
/**
* The most subjects one statement names: 600 (overview "Security", ruling R-002; the run
* statement's cap too). A signer refuses a run that pushed more; the statement schema refuses a
* longer `subject` list.
*/
const BUILD_PROVENANCE_MAX_SUBJECTS = 600;
/**
* The longest `sig` text, in characters: the base64 length of the longest DER ECDSA P-256
* signature, 72 bytes (a SEQUENCE of two 33-byte INTEGERs).
*/
const DSSE_SIG_MAX_LENGTH = 96;
/**
* The three words of `BUILD_PROVENANCE_MODE`, the CP-wide publish setting (D6, DEF-6). It decides
* when a publish with no envelope is refused; a present envelope that fails a check is refused in
* every mode (DEF-3).
*
* - `off`: a missing envelope is never refused. The shipped default.
* - `sourced`: a missing envelope is refused when the app has a live release source.
* - `enforce`: a missing envelope is always refused.
*/
const BUILD_PROVENANCE_MODES = Object.freeze([
	"off",
	"sourced",
	"enforce"
]);
/**
* The refusal codes of the publish door's provenance rule (DEF-6). release-engine adds them to its
* refusal union, and a client reads the code as text.
*
* - `build_provenance_required`: the mode requires an envelope and the body carries none.
* - `build_provenance_invalid`: {@link verifyBuildProvenance} answered `ok: false`; the wire body
*   carries the reason, one of {@link BUILD_PROVENANCE_INVALID_REASONS}.
* - `build_provenance_mismatch`: the subjects miss a digest the version names
*   ({@link provenanceSubjectsCover} answered `covered: false`).
* - `build_provenance_source_unmatched`: the external parameters match no live release source of
*   the app, or `submitterKind` is not `human` (D5).
*/
const BUILD_PROVENANCE_REFUSAL_CODES = Object.freeze([
	"build_provenance_required",
	"build_provenance_invalid",
	"build_provenance_mismatch",
	"build_provenance_source_unmatched"
]);
/**
* Why {@link verifyBuildProvenance} refused an envelope. The array order is the order of the
* first check of each reason. `malformed` has two checks, so the checks run in this order:
*
* 1. `malformed`: the envelope fails {@link buildProvenanceEnvelopeSchema}, or its `payload` is
*    not canonical base64.
* 2. `oversize`: the decoded `payload` is longer than {@link BUILD_PROVENANCE_MAX_ENVELOPE_BYTES}.
* 3. `unknown_key`: no key, or more than one key, of the key set has the signature's `keyid`.
* 4. `unsupported_key`: the key of that `kid` is not a public ES256 P-256 key WebCrypto imports.
* 5. `malformed`: `sig` is not canonical base64 or not a strict DER ECDSA signature. This check
*    runs after the two key checks, so a bad `sig` with an unknown or unsupported key answers
*    `unknown_key` or `unsupported_key`.
* 6. `bad_signature`: the signature does not verify over the pre-authentication encoding.
* 7. `statement`: the signed payload is not UTF-8 JSON that {@link buildProvenanceStatementSchema}
*    admits. This check runs only after the signature verified, so no unsigned field is read.
*/
const BUILD_PROVENANCE_INVALID_REASONS = Object.freeze([
	"malformed",
	"oversize",
	"unknown_key",
	"unsupported_key",
	"bad_signature",
	"statement"
]);
/** Canonical padded standard base64 (RFC 4648 section 4): whole quads, then at most one padded quad. */
const STANDARD_BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
/**
* The zod form of {@link DsseEnvelope}, as both publish bodies carry it (`buildProvenance`, D3).
* Strict at every level.
*
* - `payloadType`: the literal {@link BUILD_PROVENANCE_PAYLOAD_TYPE}.
* - `payload`: 1 to {@link BUILD_PROVENANCE_MAX_PAYLOAD_LENGTH} characters of canonical padded
*   standard base64.
* - `signatures`: exactly one entry; `keyid` matches the kid grammar (1 to 128 base64url
*   characters) and `sig` is 1 to 96 characters of canonical padded standard base64.
*
* A parse proves the shape and the bounds. It never decodes and never verifies: that is
* {@link verifyBuildProvenance}. No `.transform()`. No side effects.
*/
const buildProvenanceEnvelopeSchema = z.strictObject({
	payloadType: z.literal(BUILD_PROVENANCE_PAYLOAD_TYPE),
	payload: z.string().min(1).max(BUILD_PROVENANCE_MAX_PAYLOAD_LENGTH).regex(STANDARD_BASE64_PATTERN),
	signatures: z.array(z.strictObject({
		keyid: z.string().max(128).regex(KERNEL_KID_PATTERN),
		sig: z.string().min(1).max(DSSE_SIG_MAX_LENGTH).regex(STANDARD_BASE64_PATTERN)
	})).length(1)
});
/** Printable ASCII with no space, 1 to `max` characters. */
function printable(max) {
	return z.string().min(1).max(max).regex(/^[\x21-\x7e]+$/);
}
/**
* The zod form of {@link BuildProvenanceExternalParameters} (ruling R-002). Strict: a member it
* does not name is refused. zion-build parses the parameters it read from the run row with it
* before it signs; {@link buildProvenanceStatementSchema} embeds it.
*
* - `repository`: `<owner>/<name>` in lower case, the owner 1 to 39 characters of `[a-z0-9-]` that
*   start with a letter or digit, the name 1 to 100 characters of `[a-z0-9._-]` that start with a
*   letter or digit (the release-source repository grammar of `kernel-rights.contract.ts`).
* - `ref`: `refs/heads/` or `refs/tags/` and one or more non-empty segments of `[A-Za-z0-9._-]`,
*   with no `..`, at most 255 characters. A `*` is refused: a run ref is a concrete ref.
* - `workflow`: `.zion/workflows/` and a 1 to 64 character file name that ends in `.yml` or `.yaml`.
* - `sha`: 40 or 64 lower-case hex digits.
* - `runId`: 1 to 128 characters of `[A-Za-z0-9._-]`.
* - `submitterKind`: one of `SUBMITTER_KINDS` (`human`, `agent`, `system`).
*
* No `.transform()`. No side effects.
*/
const buildProvenanceExternalParametersSchema = z.strictObject({
	repository: z.string().max(140).regex(/^[a-z0-9][a-z0-9-]{0,38}\/[a-z0-9][a-z0-9._-]{0,99}$/),
	ref: z.string().max(255).regex(/^(?!.*\.\.)refs\/(?:heads|tags)(?:\/[A-Za-z0-9._-]+)+$/),
	workflow: z.string().max(85).regex(/^\.zion\/workflows\/[A-Za-z0-9._-]{1,64}\.ya?ml$/),
	sha: z.string().regex(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/),
	runId: z.string().min(1).max(128).regex(/^[A-Za-z0-9._-]+$/),
	submitterKind: z.enum(SUBMITTER_KINDS)
});
/**
* The zod form of {@link BuildProvenanceStatement}. Strict at every level: the SLSA members this
* contract does not name (`internalParameters`, `resolvedDependencies`, `byproducts`, the builder
* `version`) are refused, so the signer and every reader agree on one closed shape.
*
* - `subject`: 1 to {@link BUILD_PROVENANCE_MAX_SUBJECTS} entries; `name` is 1 to 512 printable
*   ASCII characters with no space; `digest` holds `sha256` alone, 64 lower-case hex digits.
* - `buildType`: the literal {@link ZION_BUILD_PROVENANCE_BUILD_TYPE}.
* - `externalParameters`: {@link buildProvenanceExternalParametersSchema}.
* - `builder.id`: the literal {@link ZION_BUILD_BUILDER_ID}.
* - `metadata`: optional; `invocationId` is 1 to 256 printable ASCII characters, `startedOn` and
*   `finishedOn` are RFC 3339 UTC date-times.
*
* No `.transform()`. No side effects.
*/
const buildProvenanceStatementSchema = z.strictObject({
	_type: z.literal(IN_TOTO_STATEMENT_TYPE),
	subject: z.array(z.strictObject({
		name: printable(512),
		digest: z.strictObject({ sha256: z.string().regex(/^[0-9a-f]{64}$/) })
	})).min(1).max(600),
	predicateType: z.literal(SLSA_PROVENANCE_PREDICATE_TYPE),
	predicate: z.strictObject({
		buildDefinition: z.strictObject({
			buildType: z.literal(ZION_BUILD_PROVENANCE_BUILD_TYPE),
			externalParameters: buildProvenanceExternalParametersSchema
		}),
		runDetails: z.strictObject({
			builder: z.strictObject({ id: z.literal(ZION_BUILD_BUILDER_ID) }),
			metadata: z.strictObject({
				invocationId: printable(256).optional(),
				startedOn: z.iso.datetime().optional(),
				finishedOn: z.iso.datetime().optional()
			}).optional()
		})
	})
});
/**
* The DSSE v1 pre-authentication encoding of one payload: the bytes the signer signs and the
* verifier verifies.
*
* `"DSSEv1" SP LEN(type) SP type SP LEN(body) SP body`, where `LEN` is the ASCII decimal byte
* length and `type` is the UTF-8 bytes of `payloadType`. Any type and any body, the empty ones
* included. Never throws. Pure: the input stays as it is and a new array is returned.
*/
function dssePreAuthEncoding(payloadType, payload) {
	const type = utf8ToBytes(payloadType);
	const head = utf8ToBytes(`DSSEv1 ${type.length} ${payloadType} ${payload.length} `);
	const out = new Uint8Array(head.length + payload.length);
	out.set(head, 0);
	out.set(payload, head.length);
	return out;
}
/**
* The bytes of standard base64 text that already matched {@link STANDARD_BASE64_PATTERN} (the
* envelope schema checked it), or `null` when the unused low bits of the last character are not
* zero, so one byte array has one text. Never throws.
*/
function standardBase64BytesOrNull(text) {
	try {
		return base64UrlDecode(text.replace(/=+$/, "").replaceAll("+", "-").replaceAll("/", "_"));
	} catch {
		return null;
	}
}
/**
* One DER INTEGER of an ECDSA signature at `offset`: its value with no sign byte, and the offset
* after it. `null` for a tag other than INTEGER, a length outside 1 to 33 or past the end, a
* negative value, a non-minimal encoding, or a value longer than 32 bytes.
*/
function readDerInteger(der, offset) {
	if (der[offset] !== 2) return null;
	const length = der[offset + 1] ?? 0;
	const start = offset + 2;
	if (length < 1 || length > 33 || start + length > der.length) return null;
	const bytes = der.subarray(start, start + length);
	if ((bytes[0] & 128) !== 0) return null;
	if (length > 1 && bytes[0] === 0 && (bytes[1] & 128) === 0) return null;
	const value = bytes[0] === 0 && length > 1 ? bytes.subarray(1) : bytes;
	if (value.length > 32) return null;
	return {
		value,
		next: start + length
	};
}
/**
* The raw 64-byte `r || s` form of a strict DER ECDSA P-256 signature, the form WebCrypto
* verifies. `null` for anything but one short-form SEQUENCE that holds exactly two INTEGERs and no
* trailing byte. Never throws.
*/
function derEcdsaToRaw(der) {
	if (der[0] !== 48 || der[1] !== der.length - 2) return null;
	const r = readDerInteger(der, 2);
	if (r === null) return null;
	const s = readDerInteger(der, r.next);
	if (s?.next !== der.length) return null;
	const raw = /* @__PURE__ */ new Uint8Array(64);
	raw.set(r.value, 32 - r.value.length);
	raw.set(s.value, 64 - s.value.length);
	return raw;
}
/**
* Verifies one build provenance envelope against a key set. Untrusted input. Callers:
* release-engine's publish door (WS-3) with the keys of `BUILD_PROVENANCE_JWKS`, and
* `bin/verify-build-provenance.mjs` (WS-6).
*
* Inputs: `envelope`, any value (the parsed `buildProvenance` member, or a parsed referrer blob);
* `keys`, the `keys` of a `rightsCertificateJwksSchema` document, 0 to 8 public ES256 P-256 keys.
* An empty key set refuses every envelope that passes the `malformed` and `oversize` checks of the
* payload with `unknown_key`.
*
* The checks run in the order that {@link BUILD_PROVENANCE_INVALID_REASONS} documents (the second
* `malformed` check, of `sig`, runs after the two key checks), and the first failure gives the
* answer. The signature verifies over {@link dssePreAuthEncoding} of the payload type and
* the decoded payload bytes before any statement field is read.
*
* Output: `ok: true` with the statement, the key id and the payload digest only for a valid
* signature over a statement that {@link buildProvenanceStatementSchema} admits. It does not check
* the subjects (see {@link provenanceSubjectsCover}) or the release source; those are the caller's
* rules.
*
* It never rejects for caller input, reads no clock, makes no request and writes nothing.
*/
async function verifyBuildProvenance(envelope, keys) {
	const parsed = buildProvenanceEnvelopeSchema.safeParse(envelope);
	if (!parsed.success) return {
		ok: false,
		reason: "malformed"
	};
	const payload = standardBase64BytesOrNull(parsed.data.payload);
	if (payload === null) return {
		ok: false,
		reason: "malformed"
	};
	if (payload.length > 262144) return {
		ok: false,
		reason: "oversize"
	};
	const signature = parsed.data.signatures[0];
	const named = keys.filter((key) => key.kid === signature.keyid);
	if (named.length !== 1) return {
		ok: false,
		reason: "unknown_key"
	};
	const key = await importEs256VerifyKey(named[0]);
	if (key === null) return {
		ok: false,
		reason: "unsupported_key"
	};
	const der = standardBase64BytesOrNull(signature.sig);
	const raw = der === null ? null : derEcdsaToRaw(der);
	if (raw === null) return {
		ok: false,
		reason: "malformed"
	};
	if (!await verifyEs256(key, raw, dssePreAuthEncoding(parsed.data.payloadType, payload))) return {
		ok: false,
		reason: "bad_signature"
	};
	let document;
	try {
		document = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(payload));
	} catch {
		return {
			ok: false,
			reason: "statement"
		};
	}
	const statement = buildProvenanceStatementSchema.safeParse(document);
	if (!statement.success) return {
		ok: false,
		reason: "statement"
	};
	return {
		ok: true,
		statement: statement.data,
		keyId: signature.keyid,
		payloadDigest: sha256DigestOf(payload)
	};
}
/**
* Whether a verified statement names every required digest (D4). Caller: release-engine's
* provenance rule (WS-3) with the bundle digest and the digest of every fixture image the version
* names, and the platform release check (WS-6) with the record and artifact digests.
*
* A required digest is covered when one subject's `digest.sha256` equals its hex part:
* `sha256:<hex>` is covered by a subject with `{ sha256: '<hex>' }`. An image index digest covers
* its platform children by content address, so the caller names index digests only.
*
* - An empty `required` list: `covered: true`.
* - A required value outside the `sha256:<64 lower-case hex>` grammar: never covered, so it is
*   in `missing`.
*
* Pure: reads nothing beyond its arguments. Never throws.
*/
function provenanceSubjectsCover(statement, required) {
	const named = new Set(statement.subject.map((subject) => `sha256:${subject.digest.sha256}`));
	const missing = [];
	for (const digest of required) if (!named.has(digest) && !missing.includes(digest)) missing.push(digest);
	return missing.length === 0 ? { covered: true } : {
		covered: false,
		missing
	};
}
//#endregion
export { BUILD_PROVENANCE_INVALID_REASONS, BUILD_PROVENANCE_MAX_ENVELOPE_BYTES, BUILD_PROVENANCE_MAX_PAYLOAD_LENGTH, BUILD_PROVENANCE_MAX_SUBJECTS, BUILD_PROVENANCE_MODES, BUILD_PROVENANCE_PAYLOAD_TYPE, BUILD_PROVENANCE_REFUSAL_CODES, IN_TOTO_STATEMENT_TYPE, SLSA_PROVENANCE_PREDICATE_TYPE, ZION_BUILD_BUILDER_ID, ZION_BUILD_PROVENANCE_BUILD_TYPE, buildProvenanceEnvelopeSchema, buildProvenanceExternalParametersSchema, buildProvenanceStatementSchema, dssePreAuthEncoding, provenanceSubjectsCover, verifyBuildProvenance };

//# sourceMappingURL=build-provenance.js.map